import React, { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router";
import { toast } from "sonner";
import {
  CheckCircleIcon,
  XMarkIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { Input } from "components/ui";
import { DatePicker } from "components/shared/form/Datepicker";
import {
  getClientSelectionApproval,
  uploadClientSignedDocument,
  lockClientSelectionApproval,
} from "services/clientSelectionApprovalService";
import { SelectionMediaImage } from "./components/SelectionMediaImage";
import {
  buildJobSelectionTableExportData,
  resolveJobSelectionTablePhotoUrls,
  exportJobSelectionsTableToPdf,
  buildRichComments,
  buildLocationsText,
  buildDynamicFieldRows,
  formatDateTimeUTC,
} from "./utils/templatePdfExport";
import { JobSelectionTablePrintLayout } from "./components/JobSelectionTablePrintLayout";
import sunnyHomesLogo from "assets/images/SH-logo.png";

const formatDateTime = (isoString) => formatDateTimeUTC(isoString) || "";

const getTodayDateString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForInput = (dateVal) => {
  if (!dateVal) return "";
  if (typeof dateVal === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
    return dateVal;
  }
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Interactive Signature Trackpad Component — exposes toDataURL()/isEmpty()/clear() via ref so the
// parent can pull the drawn image only at submit time (the canvas itself is the source of truth,
// not React state, to avoid re-rendering on every pointer-move).
const SignaturePad = forwardRef(function SignaturePad({ onSignatureChange, disabled }, ref) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSigned, setHasSigned] = useState(false);

  useImperativeHandle(ref, () => ({
    toDataURL: () => (hasSigned ? canvasRef.current?.toDataURL("image/png") : null),
    isEmpty: () => !hasSigned,
    clear: () => clearCanvas(),
  }));

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    // Scale coordinates to match canvas internal resolution
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCoordinates(e);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSigned(true);
    onSignatureChange(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (disabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSigned(false);
    onSignatureChange(false);
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="mb-1 text-xs font-bold text-slate-800 dark:text-dark-100 flex items-center gap-1.5">
          <PencilSquareIcon className="h-4 w-4 text-primary-600" />
          Client Initial / Signature <span className="text-red-500">*</span>
        </label>
        {hasSigned && !disabled && (
          <button
            type="button"
            onClick={clearCanvas}
            className="text-[11px] font-semibold text-red-600 hover:text-red-700 hover:underline flex items-center gap-1"
          >
            <ArrowPathIcon className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      <div className={`relative rounded-xl border border-slate-300 dark:border-dark-600 bg-white dark:bg-dark-800 overflow-hidden shadow-inner ${disabled ? "pointer-events-none opacity-60" : ""}`}>
        <canvas
          ref={canvasRef}
          width={380}
          height={120}
          className="w-full h-[140px] cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        {!hasSigned && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-slate-400 dark:text-dark-500 italic border-b border-dashed border-slate-200 dark:border-dark-700 m-4">
            Sign or draw initials here
          </div>
        )}
      </div>
    </div>
  );
});

// Terms & Conditions Modal Component
function TermsModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl rounded-2xl border border-slate-200 dark:border-dark-700 bg-white dark:bg-dark-800 shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-dark-700 px-6 py-4">
          <div className="flex items-center gap-2.5">
            <DocumentTextIcon className="h-5 w-5 text-primary-600" />
            <h3 className="text-sm font-bold text-slate-900 dark:text-dark-100">
              Terms & Conditions — Selection Sign-Off
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-dark-700"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-4 text-xs text-slate-600 dark:text-dark-300 leading-relaxed">
          <section>
            <h4 className="font-bold text-slate-800 dark:text-dark-100 mb-1">1. Finality of Selections</h4>
            <p>
              By signing and confirming these selections, the client acknowledges that all specified brands,
              ranges, colors, models, and locations are final and confirmed for procurement and construction.
            </p>
          </section>

          <section>
            <h4 className="font-bold text-slate-800 dark:text-dark-100 mb-1">2. Variations & Change Orders</h4>
            <p>
              Any changes requested after this confirmation will be treated as formal Variation Orders,
              subject to administration fees, supplier restocking costs, and potential construction time extensions.
            </p>
          </section>

          <section>
            <h4 className="font-bold text-slate-800 dark:text-dark-100 mb-1">3. Product Availability & Substitution</h4>
            <p>
              In the event that a selected item or model is discontinued or unavailable from the manufacturer/supplier,
              Sunny Homes reserves the right to present equivalent alternatives of equal quality and specification for client approval.
            </p>
          </section>

          <section>
            <h4 className="font-bold text-slate-800 dark:text-dark-100 mb-1">4. Allowances & Provisional Sums</h4>
            <p>
              Items subject to Prime Cost (PC) or Provisional Sum (PS) allowances are estimated based on contract rates.
              Final costs will be adjusted upon receipt of final supplier invoices.
            </p>
          </section>
        </div>

        <div className="border-t border-slate-200 dark:border-dark-700 bg-slate-50 dark:bg-dark-900/40 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-primary-600 text-white text-xs font-semibold hover:bg-primary-700 shadow-sm"
          >
            Close & Accept
          </button>
        </div>
      </div>
    </div>
  );
}

// Maps one item from the anonymous API's client-safe shape into the shape
// buildJobSelectionTableExportData (and the on-page table below) expect — same field names the
// authenticated SelectionsPage flow already uses (jobSelectionMappers.js's legacy shape), so the
// PDF export engine and print layout can be reused unmodified.
const mapApprovalItemForDisplay = (item, mainCategoryName) => ({
  id: item.id,
  categoryType: mainCategoryName || "Selections",
  category: item.category || "",
  type: item.type || "",
  supplier: item.brand || "",
  range: item.range || "",
  name: item.name,
  isLocked: !!item.isLocked,
  lockedAt: item.lockedAt || null,
  lockedDate: (item.isLocked && item.lockedAt) ? formatDateTime(item.lockedAt) : null,
  comments: item.comments || item.notes || "",
  locationTags: (item.locationTags || []).map((t) => ({ id: t.id, name: t.tagName || t.name })),
  customFields: item.customFields || item.dynamicFields || item.dynamicFieldValues || item.dynamicData || [],
  images: (item.media || [])
    .filter((m) => (m.mediaType ?? 1) === 1)
    .map((m) => m.fileUrl)
    .filter(Boolean),
});

export default function ClientSelectionApprovalPage() {
  const { token } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [mainCategoryName, setMainCategoryName] = useState("");
  const [items, setItems] = useState([]);
  const [isAlreadyLocked, setIsAlreadyLocked] = useState(false);

  // Sign-off states
  const [approvalDate, setApprovalDate] = useState(() => getTodayDateString());
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const [hasSignature, setHasSignature] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Captured once at submit time and kept around so the Thank You screen's "Download Confirmation
  // PDF" button can re-render the same signed-off PDF afterwards — signaturePadRef's canvas is
  // gone by then (the sign-off step is unmounted once showThankYouState flips to true).
  const [submittedSignatureDataUrl, setSubmittedSignatureDataUrl] = useState(null);
  const signaturePadRef = useRef(null);

  const getCombinedDateTimeIso = () => {
    if (!approvalDate) return new Date().toISOString();
    let dateObj;
    if (typeof approvalDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(approvalDate)) {
      const [y, m, d] = approvalDate.split("-").map(Number);
      dateObj = new Date(y, m - 1, d);
    } else {
      dateObj = new Date(approvalDate);
    }
    if (isNaN(dateObj.getTime())) return new Date().toISOString();

    const now = new Date();
    dateObj.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
    return dateObj.toISOString();
  };

  useEffect(() => {
    if (!isSubmitted) return;

    setRedirectCountdown(20);

    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev === null) return null;
        if (prev <= 1) {
          clearInterval(timer);
          window.location.replace("https://sunnyhomes.com.au/");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isSubmitted]);

  // Restore signature & approval timestamp from sessionStorage on refresh
  useEffect(() => {
    if (!token) return;
    try {
      const saved = sessionStorage.getItem(`client_approval_${token}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.signatureDataUrl) setSubmittedSignatureDataUrl(parsed.signatureDataUrl);
        if (parsed.approvalDateIso) setApprovalDate(parsed.approvalDateIso);
      }
    } catch (e) { }
  }, [token]);

  // PDF Export States
  const [pdfExportData, setPdfExportData] = useState(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const pdfPrintRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError("");

    getClientSelectionApproval(token).then((res) => {
      if (!isMounted) return;
      if (res.success && res.data) {
        setJobTitle(res.data.jobTitle || "");
        setMainCategoryName(res.data.mainCategoryName || "");
        setItems(res.data.items || []);
        setIsAlreadyLocked(!!res.data.isAlreadyLocked);
      } else {
        setLoadError(res.error || "This link is invalid or has expired.");
      }
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [token]);

  const displayItems = useMemo(
    () => items.map((item) => mapApprovalItemForDisplay(item, mainCategoryName)),
    [items, mainCategoryName],
  );

  const groupedByCategory = useMemo(() => {
    const groups = {};
    displayItems.forEach((item) => {
      const key = item.category || "";
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    const entries = Object.entries(groups).sort(([aKey], [bKey]) => {
      const aIsDirect = !aKey || aKey === "Uncategorised" || aKey === "General";
      const bIsDirect = !bKey || bKey === "Uncategorised" || bKey === "General";
      if (aIsDirect && !bIsDirect) return -1;
      if (!aIsDirect && bIsDirect) return 1;
      return 0;
    });
    return Object.fromEntries(entries);
  }, [displayItems]);

  const getDisplayJobTitle = () => jobTitle || "Home Selections";

  const buildPdfBlob = async ({ signatureDataUrl, approvalDateIso, isLockedPdf }) => {
    const title = getDisplayJobTitle();
    const builtData = buildJobSelectionTableExportData(displayItems, {
      title,
      isLockedPdf: !!isLockedPdf,
      signatureDataUrl: signatureDataUrl || null,
      approvalDate: approvalDateIso || null,
    });
    const resolvedData = await resolveJobSelectionTablePhotoUrls(builtData);
    setPdfExportData(resolvedData);

    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const result = await exportJobSelectionsTableToPdf({
      title,
      printElement: pdfPrintRef.current,
      download: false,
    });
    setPdfExportData(null);
    return result;
  };

  const handleSubmitApproval = async () => {
    if (!termsAccepted) {
      toast.error("Please accept the Terms & Conditions.");
      return;
    }
    const signatureDataUrl = signaturePadRef.current?.toDataURL();
    if (!signatureDataUrl) {
      toast.error("Please sign before submitting.");
      return;
    }
    if (displayItems.length === 0) {
      toast.error("No confirmed selections to lock.");
      return;
    }

    setIsSubmitting(true);
    try {
      const combinedIso = getCombinedDateTimeIso();
      const { blob, fileName } = await buildPdfBlob({
        signatureDataUrl,
        approvalDateIso: combinedIso,
        isLockedPdf: false,
      });

      const uploaded = await uploadClientSignedDocument(token, blob, fileName);

      const lockRes = await lockClientSelectionApproval(token, {
        ...uploaded,
        signedDate: combinedIso,
      });

      if (!lockRes.success) {
        toast.error(lockRes.error || "Failed to submit approval.");
        return;
      }

      setSubmittedSignatureDataUrl(signatureDataUrl);
      setIsSubmitted(true);
      try {
        sessionStorage.setItem(`client_approval_${token}`, JSON.stringify({
          signatureDataUrl,
          approvalDateIso: combinedIso,
        }));
      } catch (e) { }
      toast.success("Selections confirmed & locked successfully!");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to submit approval.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (isExportingPdf) return;
    if (displayItems.length === 0) {
      toast.error("No selections available to export.");
      return;
    }

    setIsExportingPdf(true);
    try {
      const savedSession = (() => {
        try {
          const raw = sessionStorage.getItem(`client_approval_${token}`);
          return raw ? JSON.parse(raw) : null;
        } catch (e) {
          return null;
        }
      })();

      const effectiveSigUrl = submittedSignatureDataUrl || savedSession?.signatureDataUrl || null;
      const lockedItemDate = displayItems.find((i) => i.lockedAt)?.lockedAt || null;
      const effectiveDateIso = savedSession?.approvalDateIso || (effectiveSigUrl ? getCombinedDateTimeIso() : lockedItemDate || getCombinedDateTimeIso());

      const isLockedPdf = !effectiveSigUrl;
      const { blob, fileName } = await buildPdfBlob({
        signatureDataUrl: effectiveSigUrl,
        approvalDateIso: effectiveDateIso,
        isLockedPdf,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success("Selections PDF exported successfully");
    } catch (err) {
      console.error("Failed to export PDF:", err);
      toast.error(err.message || "Failed to export PDF");
    } finally {
      setIsExportingPdf(false);
    }
  };

  const showThankYouState = isSubmitted || isAlreadyLocked;

  return (
    <div className={`min-h-screen bg-slate-200 dark:bg-dark-950 flex flex-col font-sans ${showThankYouState ? "h-screen justify-center p-4 sm:p-6 overflow-hidden" : "py-8 px-4 sm:px-6"}`}>
      <div className={`max-w-[850px] w-full mx-auto bg-white dark:bg-dark-900 border border-slate-300 dark:border-dark-700 shadow-2xl rounded-sm overflow-hidden text-slate-900 dark:text-dark-100 ${showThankYouState ? "p-6 sm:p-8" : "p-8 sm:p-12"}`}>

        {/* PDF Top Branding Header */}
        <div className={`flex items-start justify-between border-b border-slate-100 dark:border-dark-800 ${showThankYouState ? "pb-4 mb-4 sm:pb-5 sm:mb-5" : "pb-8 mb-6"}`}>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-dark-100 tracking-tight">
              {getDisplayJobTitle()}
            </h1>
            {mainCategoryName && (
              <p className="text-xs font-semibold text-slate-500 dark:text-dark-400 uppercase tracking-wide mt-1">
                {mainCategoryName} Selections
              </p>
            )}
          </div>

          <div className="flex items-center">
            <img
              src={sunnyHomesLogo}
              alt="Sunny Homes"
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </div>
        </div>

        {/* Document Body */}
        <div className="space-y-8">
          {isLoading ? (
            <div className="py-16 text-center space-y-3">
              <div className="w-8 h-8 border-3 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-xs text-slate-500 dark:text-dark-400">Loading selection document...</p>
            </div>
          ) : loadError ? (
            <div className="py-16 text-center space-y-3">
              <ShieldCheckIcon className="w-12 h-12 text-slate-400 mx-auto" />
              <h3 className="text-base font-bold text-slate-800 dark:text-dark-100">
                Link Unavailable
              </h3>
              <p className="text-xs text-slate-500 dark:text-dark-400 max-w-sm mx-auto">
                {loadError}
              </p>
            </div>
          ) : showThankYouState ? (
            <div className="py-2 sm:py-4 text-center space-y-4 sm:space-y-5">
              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mx-auto shadow-inner">
                <CheckCircleIcon className="h-8 w-8 sm:h-10 sm:w-10" />
              </div>

              <div className="space-y-1.5 max-w-lg mx-auto">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-dark-100">
                  Thank You!
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 dark:text-dark-300 leading-relaxed">
                  Your home selections for <span className="font-bold text-slate-900 dark:text-dark-100">{getDisplayJobTitle()}</span> have been successfully signed, confirmed, and locked.
                </p>
                <p className="text-[11px] sm:text-xs text-slate-500 dark:text-dark-400">
                  Our project management team has been notified. Selections for this category are finalized and locked for procurement.
                </p>
                {redirectCountdown !== null && (
                  <p className="text-xs font-semibold text-primary-600 dark:text-primary-400 pt-1 animate-pulse">
                    Redirecting to sunnyhomes.com.au in <span className="font-bold">{redirectCountdown}</span> second{redirectCountdown !== 1 ? "s" : ""}...
                  </p>
                )}
              </div>

              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                <ShieldCheckIcon className="h-4 w-4 text-emerald-600" />
                <span>Selections Signed & Locked</span>
              </div>

              <div className="pt-2 flex justify-center">
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={isExportingPdf}
                  className="py-3 px-5 rounded-xl font-bold text-xs sm:text-sm bg-primary-600 hover:bg-primary-700 text-white shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>{isExportingPdf ? "Generating PDF..." : "Download Confirmation PDF"}</span>
                </button>
              </div>
            </div>
          ) : displayItems.length === 0 ? (
            <div className="py-16 text-center space-y-3">
              <ShieldCheckIcon className="w-12 h-12 text-slate-400 mx-auto" />
              <h3 className="text-base font-bold text-slate-800 dark:text-dark-100">
                No Confirmed Selections Found
              </h3>
              <p className="text-xs text-slate-500 dark:text-dark-400 max-w-sm mx-auto">
                There are no confirmed selections ready for approval under this category for this project.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedByCategory).map(([catTitle, catItems]) => (
                <div key={catTitle} className="space-y-4">
                  {catTitle ? (
                    <h3 className="text-center font-bold text-slate-900 dark:text-dark-100 text-base py-1">
                      {catTitle}
                    </h3>
                  ) : null}

                  <div className="space-y-5">
                    {catItems.map((item) => {
                      const imgUrl = item.images?.[0] || null;
                      const commentsText = buildRichComments(item, { includeLocations: false });
                      const locationsText = buildLocationsText(item);
                      const dynamicFieldRows = buildDynamicFieldRows(item);

                      return (
                        <div key={item.id} className="space-y-1.5">
                          {item.type && (
                            <h4 className="text-center text-xs font-bold tracking-wide text-slate-600 uppercase dark:text-dark-300">
                              {item.type}
                            </h4>
                          )}
                          <div className="dark:border-dark-600 dark:bg-dark-900 flex flex-col overflow-hidden border border-slate-800 bg-white shadow-xs md:flex-row">
                          <div className="dark:divide-dark-600 flex-1 divide-y divide-slate-800 text-xs">
                            <div className="flex">
                              <div className="dark:text-dark-100 dark:border-dark-600 dark:bg-dark-900 w-28 shrink-0 border-r border-slate-800 bg-white p-2.5 font-bold text-slate-900">
                                Brand
                              </div>
                              <div className="dark:text-dark-200 dark:bg-dark-900 flex-1 bg-white p-2.5 text-slate-800">
                                {item.supplier || "—"}
                              </div>
                            </div>

                            <div className="flex">
                              <div className="dark:text-dark-100 dark:border-dark-600 dark:bg-dark-900 w-28 shrink-0 border-r border-slate-800 bg-white p-2.5 font-bold text-slate-900">
                                Range
                              </div>
                              <div className="dark:text-dark-200 dark:bg-dark-900 flex-1 bg-white p-2.5 text-slate-800">
                                {item.range || "—"}
                              </div>
                            </div>

                            <div className="flex">
                              <div className="dark:text-dark-100 dark:border-dark-600 dark:bg-dark-900 w-28 shrink-0 border-r border-slate-800 bg-white p-2.5 font-bold text-slate-900">
                                Option
                              </div>
                              <div className="dark:text-dark-100 dark:bg-dark-900 flex-1 bg-white p-2.5 font-semibold text-slate-900">
                                {item.name}
                              </div>
                            </div>

                            {locationsText && (
                              <div className="flex">
                                <div className="dark:text-dark-100 dark:border-dark-600 dark:bg-dark-900 w-28 shrink-0 border-r border-slate-800 bg-white p-2.5 font-bold text-slate-900">
                                  Locations
                                </div>
                                <div className="dark:text-dark-200 dark:bg-dark-900 flex-1 bg-white p-2.5 text-slate-800">
                                  {locationsText}
                                </div>
                              </div>
                            )}

                            {dynamicFieldRows.map((field, fieldIdx) => (
                              <div className="flex" key={`dyn-${fieldIdx}`}>
                                <div className="dark:text-dark-100 dark:border-dark-600 dark:bg-dark-900 w-28 shrink-0 border-r border-slate-800 bg-white p-2.5 font-bold text-slate-900">
                                  {field.label}
                                </div>
                                <div className="dark:text-dark-200 dark:bg-dark-900 flex-1 bg-white p-2.5 text-slate-800">
                                  {field.value}
                                </div>
                              </div>
                            ))}

                            <div className="flex">
                              <div className="dark:text-dark-100 dark:border-dark-600 dark:bg-dark-900 w-28 shrink-0 border-r border-slate-800 bg-white p-2.5 font-bold text-slate-900">
                                Comments
                              </div>
                              <div className="dark:text-dark-200 dark:bg-dark-900 flex-1 bg-white p-2.5 text-slate-800">
                                {commentsText}
                              </div>
                            </div>

                            {item.isLocked && item.lockedDate && (
                              <div className="flex">
                                <div className="dark:text-dark-100 dark:border-dark-600 dark:bg-dark-900 w-28 shrink-0 border-r border-slate-800 bg-white p-2.5 font-bold text-slate-900">
                                  Locked Date
                                </div>
                                <div className="dark:text-dark-200 dark:bg-dark-900 flex flex-1 items-center justify-between bg-white p-2.5 text-slate-800">
                                  <span className="dark:text-dark-100 font-semibold text-slate-900">
                                    {item.lockedDate}
                                  </span>
                                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300">
                                    <LockClosedIcon className="h-3 w-3" />{" "}
                                    Locked
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="dark:border-dark-600 dark:bg-dark-800 relative min-h-[110px] w-full shrink-0 border-t border-slate-800 bg-slate-100 md:w-[35%] md:border-t-0 md:border-l">
                            <SelectionMediaImage
                              fileKey={imgUrl}
                              alt={item.name}
                              className="absolute inset-0 h-full w-full object-cover"
                            />
                          </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Bottom Sign-off Section */}
          {!isLoading && !loadError && !showThankYouState && displayItems.length > 0 && (
            <div className="pt-8 border-t-2 border-slate-300 dark:border-dark-700 space-y-6 print:hidden">
              <h3 className="text-sm font-bold text-slate-900 dark:text-dark-100 flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5 text-primary-600" />
                Client Approval & Sign-off
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 dark:bg-dark-800/60 p-5 rounded-2xl border border-slate-200 dark:border-dark-700">
                <div className="flex flex-col gap-2">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-bold text-slate-800 dark:text-dark-100">
                        Approval Date <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <DatePicker
                      value={formatDateForInput(approvalDate)}
                      options={{ maxDate: "today" }}
                      disabled={isSubmitting}
                      onChange={(dates) => {
                        if (dates && dates[0]) {
                          const d = dates[0];
                          const yyyy = d.getFullYear();
                          const mm = String(d.getMonth() + 1).padStart(2, '0');
                          const dd = String(d.getDate()).padStart(2, '0');
                          setApprovalDate(`${yyyy}-${mm}-${dd}`);
                        } else {
                          setApprovalDate("");
                        }
                      }}
                      className="w-full cursor-pointer"
                    />
                  </div>
                </div>

                <SignaturePad ref={signaturePadRef} onSignatureChange={setHasSignature} disabled={isSubmitting} />
              </div>

              <div className="space-y-2 pt-1">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="termsCheckbox"
                    checked={termsAccepted}
                    disabled={isSubmitting}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <label
                    htmlFor="termsCheckbox"
                    className={`text-xs text-slate-700 dark:text-dark-300 leading-normal select-none ${isSubmitting ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                  >
                    I have reviewed and agree to the{" "}
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => setIsTermsModalOpen(true)}
                      className="font-bold text-primary-600 hover:underline inline-flex items-center gap-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Terms & Conditions
                    </button>{" "}
                    for the home selections listed above.
                  </label>
                </div>

                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => setIsTermsModalOpen(true)}
                  className="text-[11px] font-semibold text-primary-600 hover:text-primary-700 hover:underline flex items-center gap-1 pl-7 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  View Full Terms & Conditions
                </button>
              </div>

              <div className="pt-2 flex flex-col justify-between sm:flex-row items-center gap-3">
                <button
                  type="button"
                  onClick={handleDownloadPdf}
                  disabled={isExportingPdf || isSubmitting}
                  className="w-full sm:w-auto py-3.5 px-5 rounded-xl font-semibold text-sm border border-slate-200 dark:border-dark-700 bg-white dark:bg-dark-800 text-slate-700 dark:text-dark-200 hover:bg-slate-50 dark:hover:bg-dark-700 transition-all shadow-sm flex items-center justify-center gap-2 shrink-0 print:hidden disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download PDF"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-slate-500" />
                  <span>{isExportingPdf ? "Generating PDF..." : "Download PDF"}</span>
                </button>

                <button
                  type="button"
                  onClick={handleSubmitApproval}
                  disabled={!termsAccepted || !hasSignature || isSubmitting}
                  className={`w-full sm:w-auto py-3.5 px-6 rounded-xl font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 ${termsAccepted && hasSignature && !isSubmitting
                    ? "bg-primary-600 hover:bg-primary-700 text-white cursor-pointer shadow-primary-500/20"
                    : "bg-slate-200 dark:bg-dark-700 text-slate-400 dark:text-dark-500 cursor-not-allowed shadow-none"
                    }`}
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  {isSubmitting ? "Submitting Approval..." : "Confirm & Lock Selections"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />

      {/* Hidden off-screen printable layout rasterized for PDF generation */}
      {createPortal(
        <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
          <JobSelectionTablePrintLayout ref={pdfPrintRef} data={pdfExportData} />
        </div>,
        document.body,
      )}
    </div>
  );
}
